import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Capacitor } from '@capacitor/core'
import { selectIsAuthenticated } from '@/store/authSlice'
import { API_BASE } from '@/config/api'
import { firebaseConfig, FIREBASE_VAPID_KEY, isFirebaseWebConfigured, getFirebaseMessaging } from '@/config/firebase'

// Lanza el flujo completo de registro (permiso + SW + token FCM + backend).
// Extraído del hook para poder invocarlo también a demanda desde un click
// (ver NotificationPreferencesDialog): Safari/iOS exige que tanto
// Notification.requestPermission() como PushManager.subscribe() (dentro de
// getToken()) ocurran dentro de un gesto de usuario reciente, si no fallan en
// silencio — por eso el intento automático tras el login (más abajo) puede no
// funcionar en iOS aunque sí funcione en Chrome de escritorio.
export const registerWebPushToken = async (token) => {
    if (!('serviceWorker' in navigator) || typeof Notification === 'undefined') {
        throw new Error('Este navegador no soporta notificaciones push')
    }

    const messaging = await getFirebaseMessaging()
    if (!messaging) {
        throw new Error('Este navegador no soporta Firebase Messaging (revisa Safari/iOS: hace falta iOS 16.4+ y tener la web añadida a la pantalla de inicio)')
    }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
        throw new Error(`Permiso de notificaciones denegado (${permission})`)
    }

    // La config viaja por querystring porque public/*.js no pasa por Vite
    // (ver comentario en firebase-messaging-sw.js).
    const swParams = new URLSearchParams(firebaseConfig)
    const swRegistration = await navigator.serviceWorker.register(
        `/firebase-messaging-sw.js?${swParams.toString()}`
    )

    const { getToken, onMessage } = await import('firebase/messaging')
    const fcmToken = await getToken(messaging, {
        vapidKey: FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: swRegistration
    })
    if (!fcmToken) {
        throw new Error('No se pudo obtener el token de FCM')
    }

    const res = await fetch(`${API_BASE}/push/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-access-token': token },
        body: JSON.stringify({ token: fcmToken, platform: 'web' })
    })
    if (!res.ok) {
        throw new Error(`El servidor rechazó el registro del token (HTTP ${res.status})`)
    }

    // Con la pestaña en foreground, FCM no muestra nada
    // automáticamente (eso solo lo hace el service worker con
    // mensajes en background) — hay que crear la Notification a mano.
    onMessage(messaging, (payload) => {
        const { title, body } = payload.notification || {}
        if (title && Notification.permission === 'granted') {
            new Notification(title, { body, data: payload.data })
        }
    })

    return fcmToken
}

/**
 * Hook equivalente a usePushNotifications.js (Capacitor) pero para navegador:
 * registra el service worker de FCM, pide permiso, obtiene el token web y lo
 * manda al backend con platform: 'web'. Ver /api/push/register.
 */
export const useWebPushNotifications = () => {
    const isAuthenticated = useSelector(selectIsAuthenticated)
    const token = useSelector((state) => state.auth.token)
    const registered = useRef(false)

    useEffect(() => {
        // Capacitor ya tiene su propio hook con el SDK nativo; en navegador
        // seguimos aquí solo si Firebase Web está configurado (ver firebase.js).
        if (Capacitor.isNativePlatform()) return
        if (!isAuthenticated || !token) return
        if (registered.current) return
        if (!isFirebaseWebConfigured) return
        if (!('serviceWorker' in navigator) || typeof Notification === 'undefined') return

        registerWebPushToken(token)
            .then(() => { registered.current = true })
            .catch((err) => console.error('Web push registration failed:', err))
    }, [isAuthenticated, token])
}
